import {OVERVIEW_TABLE_PERIODS, SWR_IMMUTABLE_OPTIONS} from 'appConstants';
import useParseURL from './useParseURL';
import {timeDays, timeSundays} from 'd3-time';
import {add} from 'date-fns';
import {useGetPlan} from './swr';
import {dateReader} from 'helpers/UnitsFormatHandler';
import {ResolutionEnum} from 'interfaces';
import {useMemo} from 'react';
import {type IFilterContext, useFilterContext} from 'context/FilterContext';

export const useGetOverviewTableRange = () => {
  const {planId, overviewTableResolution} = useParseURL();
  const {data: plan} = useGetPlan({
    resourceId: planId,
    localOptions: SWR_IMMUTABLE_OPTIONS,
  });
  const filterContext = useFilterContext();

  const startDate = useMemo(() => (plan ? dateReader(plan.startDate) : undefined), [plan]);
  const planEndDate = useMemo(() => (plan ? dateReader(plan.endDate) : undefined), [plan]);
  const overviewTableColumns = filterContext?.overviewTableColumns
    ? parseInt(filterContext.overviewTableColumns)
    : OVERVIEW_TABLE_PERIODS;

  const range = useMemo(
    () => rangeHandler(overviewTableColumns, overviewTableResolution, startDate, planEndDate),
    [overviewTableResolution, plan, overviewTableColumns],
  );

  const weeklyPeriod: {startDate: Date; endDate: Date} | undefined = useMemo(
    () => (startDate && planEndDate ? weeklyPeriodHandler(overviewTableColumns, startDate, planEndDate) : undefined),
    [plan],
  );

  const tableRange = useMemo(
    () => ({startDate, range, weeklyPeriod, overviewTableResolution}),
    [startDate, range, weeklyPeriod, overviewTableResolution],
  );
  return tableRange;
};

export const useGetFakeOverviewTableRange = (filterContext: IFilterContext) => {
  const overviewTableColumns = filterContext?.overviewTableColumns
    ? parseInt(filterContext.overviewTableColumns)
    : OVERVIEW_TABLE_PERIODS;
  return timeDays(new Date(), add(new Date(), {days: overviewTableColumns}));
};

const rangeHandler = (
  overviewTableColumns: number,
  overviewTableResolution: ResolutionEnum,
  startDate?: Date,
  planEndDate?: Date,
) => {
  const rangePeriod =
    overviewTableResolution === ResolutionEnum.day ? {days: overviewTableColumns} : {weeks: overviewTableColumns};
  const rangeCalculator = overviewTableResolution === ResolutionEnum.day ? timeDays : timeSundays;
  if (!startDate || !planEndDate) {
    return;
  }
  const rangeEndDate = add(startDate, rangePeriod);
  const endDate = rangeEndDate.getTime() > planEndDate?.getTime() ? planEndDate : rangeEndDate;
  const range = rangeCalculator(startDate, endDate);

  return range;
};

const weeklyPeriodHandler = (overviewTableColumns: number, startDate: Date, planEndDate: Date) => {
  const weeklyPeriodEndDate = add(startDate, {weeks: overviewTableColumns});
  const endDate = weeklyPeriodEndDate.getTime() > planEndDate.getTime() ? planEndDate : weeklyPeriodEndDate;
  return {startDate, endDate};
};
